import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CheckCircleOutline,
  EmailOutlined,
  PeopleOutline,
  PersonAddOutlined,
  ScheduleOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { blue, green, grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Icon,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { isArray, isNil, size } from "lodash";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import GenericBlockEmailEditor from "../../components/Email/GenericBlockEmailEditor";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeaderWithSearch from "../../components/layout/TitledHeaderWithSearch";
import BasicModal from "../../components/modals/BasicModal";
import Txt from "../../components/typography/Txt";
import { useBorrowerPortalHook } from "../../hooks/useBorrowerPortalHook";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import { CACHED_DATA_REDUCER_TYPES } from "../../redux/reducers/cachedDataReducer";
import { colDefFilterMultiSelect } from "../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { sideBarColumnsFilters } from "../../utils/agGrid/sideBar/sideBarColumnsFilters";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import BorrowerDetailsSidebar from "./sidebars/BorrowerDetailsSidebar/BorrowerDetailsSidebar";
import InviteBorrowerSidebar from "./sidebars/InviteBorrowerSidebar/InviteBorrowerSidebar";
import TabSwitcher from "../../components/navigation/TabSwitcher";
import ScreenRoot from "../../components/layout/ScreenRoot";
import FlxGrid from "../../_src_shared/components/layout/FlxGrid";
import EnhancedTabSwitcher from "../../components/navigation/EnhancedTabSwitcher";
import FilterTabSwitcher from "../../components/navigation/FilterTabSwitcher";
import SearchInput from "../../components/inputs/SearchInput";
import TitledHeader from "../../components/layout/TitledHeader";
import UserEmailAvatarRow from "../../components/ui/UserEmailAvatarRow";

const CACHED_DATA_KEY = CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS;
const CACHED_DATA_KEY_STAGED =
  CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED;

// ─── Filter Keys ─────────────────────────────────────────────────────────────
const FILTER_KEYS = {
  ACTIVE: "active",
  STAGED: "staged",
  STAGED_INACTIVE: "staged_inactive",
  STAGED_INVITED: "staged_invited",
  TOTAL: "total",
};

// ─── Main Screen ─────────────────────────────────────────────────────────────

const TAB_TYPES = {
  All: "All Users",
  MyUsers: "My Users",
};
const BorrowerPortalUsersScreen = () => {
  const borrowerPortalUsers = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );
  const borrowerPortalUsersStaged = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY_STAGED],
  );
  const currentUserEmail = useSelector((state) => state.user?.emailAddress);
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const [lastSynced, setLastSynced] = useState(null);
  const [tab, setTab] = useState(TAB_TYPES.ACTIVE);
  const [filter, setFilter] = useState(FILTER_KEYS.TOTAL);
  const [quickFilter, setQuickFilter] = useState(null);

  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ref = useRef();

  const { getBorrowerPortalUsers, getAllStagedBorrowers, loading } =
    useBorrowerPortalHook();
  const { getUserPipeline } = usePipelineHook();

  const handleRefresh = () => {
    dispatch(cachedDataItemSet(CACHED_DATA_KEY, null));
    dispatch(cachedDataItemSet(CACHED_DATA_KEY_STAGED, null));
  };

  const activeUser = useMemo(() => {
    return borrowerPortalUsers?.filter((u) => u?._id === id);
  }, [borrowerPortalUsers, id]);

  // Counts for tab labels (independent of which tab is active)
  const allUsersCount = useMemo(() => {
    return size(borrowerPortalUsers);
  }, [borrowerPortalUsers]);
  const myUsersCount = useMemo(() => {
    const activeMatch = borrowerPortalUsers?.filter(
      (u) =>
        isArray(u?.permittedUsers) &&
        u.permittedUsers.includes(currentUserEmail),
    );
    const stagedMatch = borrowerPortalUsersStaged?.filter(
      (u) =>
        isArray(u?.permittedUsers) &&
        u.permittedUsers.includes(currentUserEmail),
    );
    return size(activeMatch) + size(stagedMatch);
  }, [borrowerPortalUsers, borrowerPortalUsersStaged, currentUserEmail]);

  const tabScopedData = useMemo(() => {
    const active = borrowerPortalUsers || [];
    const staged = borrowerPortalUsersStaged || [];

    if (tab === TAB_TYPES.MyUsers) {
      const myFilter = (u) =>
        isArray(u?.permittedUsers) &&
        u.permittedUsers.includes(currentUserEmail);
      return {
        active: active.filter(myFilter),
        staged: staged.filter(myFilter),
      };
    }

    return { active, staged };
  }, [tab, borrowerPortalUsers, borrowerPortalUsersStaged, currentUserEmail]);

  useEffect(() => {
    if (isArray(borrowerPortalUsers)) {
      return;
    }

    if (isNil(borrowerPortalUsers)) {
      getBorrowerPortalUsers({
        onSuccessFn: () => {
          setLastSynced(new Date().toLocaleString());
        },
      });

      getAllStagedBorrowers({
        onSuccessFn: () => {
          setLastSynced(new Date().toLocaleString());
        },
      });
    }
  }, [borrowerPortalUsers]);

  useEffect(() => {
    if (!isNil(loanPipeline)) {
      return;
    }
    if (isNil(loanPipeline)) {
      getUserPipeline({
        onSuccessFn: (data) => {
          dispatch(loanPipelineSet(data));
        },
      });
    }
  }, [loanPipeline]);

  const filteredRowData = useMemo(() => {
    const { active, staged } = tabScopedData;

    switch (filter) {
      case FILTER_KEYS.ACTIVE:
        // Active = anyone in borrowerPortalUsers (the active collection)
        return borrowerPortalUsers;
        return active;
      case FILTER_KEYS.STAGED:
        // Staged = anyone in borrowerPortalUsersStaged
        return borrowerPortalUsersStaged;
        return staged;
      case FILTER_KEYS.STAGED_INACTIVE:
        // Staged & Inactive = staged users whose activeStatus is not "active"
        return staged.filter((u) => u?.stagingStatus === "staged");
      case FILTER_KEYS.STAGED_INVITED:
        // Staged & Invited = staged users who have had an invite sent
        return staged.filter((u) => u?.stagingStatus === "invited");
      case FILTER_KEYS.TOTAL:
      default:
        // Total = both collections combined
        return [...active, ...staged];
    }
  }, [filter, tabScopedData, borrowerPortalUsersStaged, borrowerPortalUsers]);

  // Determine which column layout to use based on filter
  const columnMode = useMemo(() => {
    if (
      filter === FILTER_KEYS.STAGED ||
      filter === FILTER_KEYS.STAGED_INACTIVE ||
      filter === FILTER_KEYS.STAGED_INVITED
    ) {
      return "staged";
    }
    return "default";
  }, [filter]);

  const onRowClicked = useCallback((props) => {
    navigate(`/users/${props.data._id}`);
  }, []);

  return (
    <ScreenRoot>
      <TitledHeader
        title="Borrower Portal Users"
        // sx={{ pb: 0 }}
        titleEndContent={
          <Box sx={{ position: "relative" }}>
            <Flx
              ac
              gap={0.5}
              sx={{
                position: "absolute",
                width: "max-content",
                top: -14,
                left: 0,
              }}
            >
              <Tooltip title="Refresh Quick Applications">
                <IconButton size="small" onClick={handleRefresh}>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SyncOutlined className="thin" />
                  )}
                </IconButton>
              </Tooltip>
              {lastSynced && <Txt secondary>Last Synced: {lastSynced}</Txt>}
            </Flx>
          </Box>
        }
        endContent={
          <Flx g={1}>
            <Button
              size="large"
              startIcon={<PersonAddOutlined />}
              onClick={() => navigate("/users/invite")}
            >
              Create Borrower Account
            </Button>
          </Flx>
        }
        titleBottomContent={
          <Txt fontSize={13} color={grey[700]}>
            This page is under active development — some features may not yet be
            available or work as expected.
          </Txt>
        }
      >
        {/* <TabSwitcher
          value={tab}
          sx={{
            mt: 1,
            ml: -1,
            button: {
              // fontSize: "14px",
            },
          }}
          onChange={setTab}
          tabs={[
            {
              value: TAB_TYPES.All,
              label: `All Users`,
              // label: `All Users (${allUsersCount})`,
            },
            {
              value: TAB_TYPES.MyUsers,
              label: `My Users`,
              // label: `My Users (${myUsersCount})`,
            },
          ]}
        /> */}
      </TitledHeader>

      <Flx sx={{ py: 3 }}>
        <Container maxWidth="xl">
          <Flx column g={2}>
            {/* <TabSwitcher
              value={tab}
              sx={{
                button: {
                  fontSize: "14px",
                },
              }}
              onChange={setTab}
              tabs={[
                {
                  value: TAB_TYPES.All,
                  label: `All Users (${allUsersCount})`,
                },
                {
                  value: TAB_TYPES.MyUsers,
                  label: `My Users (${myUsersCount})`,
                },
              ]}
                
            /> */}
            <SearchInput
              variant="paper"
              fullWidth
              rounded
              value={quickFilter}
              onChange={setQuickFilter}
            />
            <SummaryItemWidgets
              tabScopedData={tabScopedData}
              activeFilter={filter}
              onFilterChange={setFilter}
            />
            <Flx column g={1} sx={{ mt: 2 }}>
              <BorrowerPortalUsersTable
                loading={
                  loading ||
                  (isNil(borrowerPortalUsers) &&
                    isNil(borrowerPortalUsersStaged))
                }
                columnMode={columnMode}
                ref={ref}
                activeId={id}
                rowData={filteredRowData}
                onRowClicked={onRowClicked}
                quickFilter={quickFilter}
              />
            </Flx>
          </Flx>
        </Container>
      </Flx>
      {id && id !== "invite" && (
        <BorrowerDetailsSidebar
          borrower={activeUser}
          onClose={() => navigate("/users")}
        />
      )}
      {id && id === "invite" && (
        <InviteBorrowerSidebar
          borrower={activeUser}
          onClose={() => navigate("/users")}
        />
      )}
    </ScreenRoot>
  );
};

const BorrowerPortalUsersTable = forwardRef(
  (
    {
      quickFilter,
      rowData,
      showAll = false,
      onRowClicked,
      loading,
      activeId,
      columnMode,
    },
    ref,
  ) => {
    const users = useSelector((state) => state.users);
    const columnTypes = useColumnTypes();
    const sideBar = useMemo(() => {
      if (showAll) {
        return sideBarColumnsFilters();
      }
      return null;
    }, [showAll]);

    const columnDefs = useMemo(() => {
      if (columnMode === "staged") {
        return [
          {
            headerName: "Created Date",
            type: "date",
            field: "createdDate",
            width: 150,
            sort: "desc",
            minWidth: 150,
            flex: 1,
          },
          {
            headerName: "Borrower Name",
            valueGetter: (params) =>
              `${params.data.firstName} ${params.data.lastName}`,
          },
          {
            headerName: "Borrower Email",
            field: "emailAddress",
            width: 200,
            minWidth: 200,
            flex: 1,
            tooltipValueGetter: (p) => p?.data?.emailAddress,
          },
          {
            headerName: "Borrower Phone",
            field: "phone",
            width: 200,
            minWidth: 200,
            flex: 1,
            valueFormatter: (params) => formatPhone(params.value),
          },
          {
            headerName: "Staging Status",
            field: "stagingStatus",
            width: 140,
            minWidth: 140,
            flex: 1,
          },
          {
            headerName: "Staged By",
            valueGetter: (params) => {
              const su = params.data?.staging_user;
              if (su?.fullName) return su.fullName;
              if (su?.firstName)
                return `${su.firstName} ${su.lastName || ""}`.trim();
              return params.data?.createdBy || "";
            },
            width: 180,
            minWidth: 180,
            flex: 1,
            tooltipValueGetter: (p) => p?.data?.createdBy,
          },
          {
            headerName: "Invite Sent",
            field: "inviteSent",
            width: 120,
            minWidth: 120,
            flex: 1,
            cellRenderer: (p) => (
              <Flx ac sx={{ height: "100%" }}>
                <Checkbox
                  checked={!!(p.value || p?.data?.invite_sent)}
                  disabled
                />
              </Flx>
            ),
          },
          {
            headerName: "Has Signed In",
            field: "hasSignedIn",
            width: 130,
            minWidth: 130,
            flex: 1,
            cellRenderer: (p) => (
              <Flx ac sx={{ height: "100%" }}>
                <Checkbox checked={!!p.value} disabled />
              </Flx>
            ),
          },
        ];
      }

      // Default columns for Active / Total tabs
      return [
        {
          headerName: "Created Date",
          type: "date",
          field: "createdDate",
          width: 150,
          sort: "desc",
          minWidth: 150,
          flex: 0,
        },
        {
          headerName: "Borrower Name",
          valueGetter: (params) =>
            `${params.data.firstName} ${params.data.lastName}`,
        },
        {
          headerName: "Borrower Email",
          field: "emailAddress",
          width: 200,
          minWidth: 200,
          flex: 0,
          tooltipValueGetter: (p) => {
            return p?.data?.emailAddress;
          },
        },
        {
          headerName: "Borrower Phone",
          field: "phone",
          width: 200,
          minWidth: 200,
          flex: 0,
          valueFormatter: (params) => formatPhone(params.value),
        },
        {
          headerName: "Last Accessed",
          type: "date",
          field: "lastAccessed",
          width: 140,
          minWidth: 140,
          flex: 0,
        },
        {
          headerTooltip: "Permitted Users",
          headerName: "Permitted Users",
          field: "permittedUsers",

          flex: 0,
          width: 145,
          cellClass: "ag-cell-vertical-center",
          minWidth: 145,
          cellRenderer: (p) => {
            return (
              <UserEmailAvatarRow
                userEmails={p?.data?.permittedUsers}
                emptyLabel="None"
              />
            );
          },
        },
        {
          headerName: "Last Updated",
          type: "date",
          field: "updatedDate",
          width: 140,
          minWidth: 140,
          flex: 0,
          tooltipValueGetter: (p) => {
            if (isNil(p?.data?.updatedDate)) {
              return null;
            }
            if (
              getUserFullNameFromEmail(users, p?.data?.updatedBy) ===
              p?.data?.updatedBy
            ) {
              return `${formatDateShort(p?.data?.updatedDate)} by ${getUserFullNameFromEmail(users, p?.data?.updatedBy)}`;
            }
            return `${formatDateShort(p?.data?.updatedDate)} by ${getUserFullNameFromEmail(users, p?.data?.updatedBy)} <${(users, p?.data?.updatedBy)}>`;
          },
          valueFormatter: (p) => {
            if (isNil(p?.data?.updatedDate)) {
              return null;
            }
            return `${formatDateShort(p?.data?.updatedDate)} by ${getUserFullNameFromEmail(users, p?.data?.updatedBy)}`;
          },
        },

        {
          headerTooltip: "Loans",
          headerName: "Loans",
          field: "dealIds",
          valueFormatter: (v) => size(v?.value) || 0,
          flex: 0,
          width: 90,
          minWidth: 90,
        },
        {
          headerTooltip: "Credit Auths",
          headerName: "Credit Auths",
          field: "creditAuths",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 120,
          minWidth: 120,
        },
        {
          headerName: "Entites",
          field: "associatedEntities",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 100,
          minWidth: 100,
        },
        {
          headerTooltip: "Credit Pulls",
          headerName: "Credit Pulls",
          field: "creditPulls",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 120,
          minWidth: 120,
        },
        {
          headerName: "Documents",
          field: "borrowerDocuments",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 110,
          minWidth: 110,
        },
        {
          headerTooltip: "Appraisal Orders",
          headerName: "Appraisal Orders",
          field: "appraisalOrders",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 120,
          minWidth: 120,
        },
        {
          headerName: "Comments",
          field: "borrowerComments",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 120,
          minWidth: 120,
        },
        {
          headerTooltip: "Experience Records",
          headerName: "Experience Records",
          field: "validatedExperience",
          valueFormatter: (v) => size(v?.value) || 0,
          width: 120,
          minWidth: 120,
        },
        {
          headerName: "Profile Complete",
          field: "profileComplete",
          width: 150,
          minWidth: 150,
          cellRenderer: (p) => {
            return (
              <Flx center sx={{ height: "100%" }}>
                <Checkbox checked={p.value} disabled />
              </Flx>
            );
          },
        },
      ];
    }, [columnMode, users]);

    const handleOnRowClicked = useCallback((props) => {
      onRowClicked(props);
    }, []);

    const defaultColDef = useMemo(() => {
      return {
        enableRowGroup: true,
        floatingFilter: false,
        filter: colDefFilterMultiSelect,
        showRowGroup: false,
        suppressHeaderFilterButton: true,
        suppressMenu: true,
        cellStyle: { cursor: "pointer" },
        flex: 1,
        minWidth: 200,
      };
    }, []);

    const getRowStyle = useCallback(
      (props) => {
        if (activeId === props.data?._id) {
          return {
            borderLeft: `2px solid ${blue[600]}`,
            backgroundColor: blue[50],
          };
        }
      },
      [activeId],
    );

    return (
      <AgGridTableWrapper compactTable sx={{ flexGrow: 1, width: "auto" }}>
        <AgGridReact
          rowData={rowData}
          ref={ref}
          columnDefs={columnDefs}
          getRowStyle={getRowStyle}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          tooltipShowDelay={100}
          pagination={true}
          paginationPageSize={20}
          domLayout="autoHeight"
          onRowClicked={handleOnRowClicked}
          groupDisplayType={"groupRows"}
          columnTypes={columnTypes}
          loading={loading}
          sideBar={sideBar}
        />
      </AgGridTableWrapper>
    );
  },
);

const SummaryItemWidgets = ({
  tabScopedData,
  activeFilter,
  onFilterChange,
}) => {
  const borrowerPortalUsers = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );
  const borrowerPortalUsersStaged = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY_STAGED],
  );
  return (
    <FlxGrid width="140px">
      <SummaryItem
        color={green[800]}
        bgColor={green[50]}
        // color="#10B981"
        // bgColor="#ECFDF5"
        icon={<CheckCircleOutline />}
        label="All Active"
        isActive={activeFilter === FILTER_KEYS.ACTIVE}
        onClick={() => onFilterChange(FILTER_KEYS.ACTIVE)}
        showLoadingWhenNil
        showLoadingWhenNilValue={borrowerPortalUsers}
        value={borrowerPortalUsers}
        // value={tabScopedData.active}
        formatValue={(v) => size(v)}
      />
      <SummaryItem
        color="#F59E0B"
        bgColor="#FFFBEB"
        icon={<ScheduleOutlined />}
        label="All Staged"
        isActive={activeFilter === FILTER_KEYS.STAGED}
        onClick={() => onFilterChange(FILTER_KEYS.STAGED)}
        showLoadingWhenNil
        showLoadingWhenNilValue={borrowerPortalUsersStaged}
        value={borrowerPortalUsersStaged}
        // value={tabScopedData.staged}
        formatValue={(v) => size(v)}
      />
      <SummaryItem
        color="#f52a0b"
        bgColor="#ffeceb"
        icon={<ScheduleOutlined />}
        label="Staged Only"
        isActive={activeFilter === FILTER_KEYS.STAGED_INACTIVE}
        onClick={() => onFilterChange(FILTER_KEYS.STAGED_INACTIVE)}
        showLoadingWhenNil
        showLoadingWhenNilValue={borrowerPortalUsersStaged}
        value={borrowerPortalUsersStaged?.filter(
          (u) => u?.stagingStatus === "staged",
        )}
        // value={borrowerPortalUsersStaged}
        // value={tabScopedData.staged}
        formatValue={(v) =>
          size(v?.filter((u) => u?.stagingStatus === "staged"))
        }
      />
      <SummaryItem
        color="#8B5CF6"
        bgColor="#F5F3FF"
        icon={<EmailOutlined />}
        label="Staged & Invited"
        isActive={activeFilter === FILTER_KEYS.STAGED_INVITED}
        onClick={() => onFilterChange(FILTER_KEYS.STAGED_INVITED)}
        showLoadingWhenNil
        showLoadingWhenNilValue={borrowerPortalUsersStaged}
        // value={borrowerPortalUsersStaged}
        value={borrowerPortalUsersStaged?.filter(
          (u) => u?.stagingStatus === "invited",
        )}
        formatValue={(v) =>
          size(v?.filter((u) => u?.inviteSent || u?.invite_sent))
        }
      />
      <SummaryItem
        color="#3B82F6"
        bgColor="#EFF6FF"
        icon={<PeopleOutline />}
        label="Total"
        isActive={activeFilter === FILTER_KEYS.TOTAL}
        onClick={() => onFilterChange(FILTER_KEYS.TOTAL)}
        showLoadingWhenNil
        showLoadingWhenNilValue={borrowerPortalUsers}
        value={[...tabScopedData.active, ...tabScopedData.staged]}
        formatValue={(v) => size(v)}
      />
    </FlxGrid>
  );
};

const SummaryItem = ({
  color,
  bgColor,
  icon,
  label,
  value,
  formatValue,
  loading,
  showLoadingWhenNil = false,
  isActive = false,
  onClick,
}) => {
  const isLoading = loading || (showLoadingWhenNil && isNil(value));

  const displayValue = isLoading ? (
    <Skeleton variant="text" width={32} height={32} sx={{ borderRadius: 1 }} />
  ) : formatValue ? (
    formatValue(value)
  ) : (
    value
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        border: "2px solid",
        borderColor: isActive ? color : "divider",
        bgcolor: "background.paper",
        // bgcolor: isActive ? bgColor : "background.paper",
        transition: "all 0.2s ease",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 4px 16px rgba(0,0,0,0.08)`,
          borderColor: isActive ? color : grey[400],
        },
        "&:active": {
          transform: "translateY(0px)",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: bgColor,
          color: color,
          flexShrink: 0,
          svg: { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: isActive ? color : "text.secondary",
            fontWeight: isActive ? 600 : 500,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.7rem",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.5rem",
            lineHeight: 1.2,
            mt: 0.25,
            color: isActive ? color : "text.primary",
          }}
        >
          {displayValue}
        </Typography>
      </Box>
    </Box>
  );
};

export default BorrowerPortalUsersScreen;
